import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart3,
  Plus,
  Trash2,
  Pencil,
  Eye,
  Wrench,
  Loader2,
  Menu,
} from 'lucide-react';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useChatLayout } from '@/components/layout/ChatLayout';
import { dashboardApi } from '@/services/dashboard-api';
import { useTimezone } from '@/contexts/TimezoneContext';
import toast from 'react-hot-toast';
import type { Dashboard, DashboardStatus } from '@/types/dashboard';

type StatusFilter = 'all' | 'building' | 'saved';

const STATUS_FILTERS: { key: StatusFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'building', label: 'Building' },
  { key: 'saved', label: 'Saved' },
];

const DashboardHomePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatRelativeTime } = useTimezone();
  const { isMobile, toggleSidebar } = useChatLayout();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const filterParam: DashboardStatus | undefined =
    statusFilter === 'all' ? undefined : statusFilter;

  const { data: result, isLoading } = useQuery({
    queryKey: ['dashboards', statusFilter],
    queryFn: () =>
      dashboardApi.list(filterParam ? { status: filterParam } : undefined),
  });

  const deleteMutation = useMutation({
    mutationFn: dashboardApi.remove,
    onSuccess: () => {
      toast.success('Dashboard deleted');
      queryClient.invalidateQueries({ queryKey: ['dashboards'] });
    },
    onError: () => {
      toast.error('Failed to delete dashboard');
    },
  });

  const dashboards: Dashboard[] = result?.data ?? [];

  const handleDelete = (
    id: string,
    name: string,
    e: React.MouseEvent
  ): void => {
    e.stopPropagation();
    e.preventDefault();
    if (
      window.confirm(
        `Are you sure you want to delete "${name}"? This action cannot be undone.`
      )
    ) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 sm:py-12 relative">
      {isMobile && (
        <button
          onClick={toggleSidebar}
          className="absolute top-4 left-4 p-2 rounded-lg text-muted-foreground hover:bg-accent transition-colors"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="w-full max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold text-foreground">
                Dashboards
              </h1>
              <p className="text-muted-foreground mt-1">
                Build, view, and manage your custom dashboards
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboards/new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Dashboard
          </button>
        </div>

        {/* Status filter tabs */}
        <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit border border-transparent dark:border-white/10">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors ' +
                (statusFilter === f.key
                  ? 'bg-white dark:bg-white/10 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Dashboard list */}
        {dashboards.length === 0 ? (
          <div className="rounded-xl bg-muted border-2 border-dashed border-input dark:border-white/10">
            <div className="p-12 text-center">
              <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {statusFilter === 'all'
                  ? 'No Dashboards Yet'
                  : `No ${statusFilter} dashboards`}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Create your first AI-powered dashboard. Describe what you want to
                see and let the AI build it for you.
              </p>
              <button
                onClick={() => navigate('/dashboards/new')}
                className="flex items-center gap-2 mx-auto px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Your First Dashboard
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {dashboards.map((dash) => (
              <DashboardCard
                key={dash.id}
                dashboard={dash}
                formatRelativeTime={formatRelativeTime}
                onDelete={handleDelete}
                isDeleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface DashboardCardProps {
  dashboard: Dashboard;
  formatRelativeTime: (date: string) => string;
  onDelete: (id: string, name: string, e: React.MouseEvent) => void;
  isDeleting: boolean;
}

const DashboardCard: React.FC<DashboardCardProps> = ({
  dashboard,
  formatRelativeTime,
  onDelete,
  isDeleting,
}) => {
  const isSaved = dashboard.status === 'saved';
  const primaryHref = isSaved
    ? `/dashboards/${dashboard.id}`
    : `/dashboards/${dashboard.id}/build`;

  return (
    <div className="rounded-xl bg-muted border border-border hover:bg-accent/50 dark:hover:bg-white/[0.07] transition-colors">
      <div className="p-5">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <Link
            to={primaryHref}
            className="text-base font-semibold text-foreground hover:text-primary truncate transition-colors"
          >
            {dashboard.name}
          </Link>
          <Badge
            variant={isSaved ? 'success' : 'neutral'}
          >
            {dashboard.status}
          </Badge>
        </div>

        {/* Description */}
        {dashboard.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {dashboard.description}
          </p>
        )}

        {/* Metadata */}
        <div className="text-xs text-muted-foreground mb-4">
          {dashboard.created_by_name && (
            <span>Created by {dashboard.created_by_name} · </span>
          )}
          Updated {formatRelativeTime(dashboard.updated_at)}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-border">
          {isSaved ? (
            <>
              <Link
                to={`/dashboards/${dashboard.id}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-accent text-primary hover:bg-accent transition-colors"
              >
                <Eye className="w-3.5 h-3.5" />
                View
              </Link>
              <Link
                to={`/dashboards/${dashboard.id}/build`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-muted-foreground hover:bg-accent dark:hover:bg-white/10 transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </Link>
            </>
          ) : (
            <Link
              to={`/dashboards/${dashboard.id}/build`}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-accent text-primary hover:bg-accent transition-colors"
            >
              <Wrench className="w-3.5 h-3.5" />
              Continue Building
            </Link>
          )}

          <button
            onClick={(e) => onDelete(dashboard.id, dashboard.name, e)}
            disabled={isDeleting}
            className="ml-auto p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/15 disabled:opacity-50 transition-colors"
            title="Delete dashboard"
          >
            {isDeleting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Trash2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHomePage;
